import React, { useMemo, useState } from "react";

/**
 * Sprint 60.131 — Ownership Curve Viz.
 *
 * Renders a proj-vs-own scatter with 4 quadrants:
 *   • Sharp    (high proj, low own)  — top-left, GREEN
 *   • Chalky   (high proj, high own) — top-right, YELLOW
 *   • Trap     (low proj, high own)  — bottom-right, RED
 *   • Value    (low proj, low own)   — bottom-left, ZINC
 *
 * Quadrant boundaries are set to the pool's median proj and median own
 * so the visualization adapts to any slate. Users can hover a dot to
 * see the player, and click to focus (highlights row in the pool
 * table via a shared callback).
 */
export default function OwnershipCurveViz({ pool = [], onPlayerClick, highlightIds = new Set() }) {
  const [tooltip, setTooltip] = useState(null);
  const [posFilter, setPosFilter] = useState("ALL");

  const filtered = useMemo(() => {
    if (posFilter === "ALL") return pool;
    return pool.filter((p) => (p.position || "").toUpperCase() === posFilter);
  }, [pool, posFilter]);

  const stats = useMemo(() => {
    if (filtered.length === 0) return null;
    const projs = filtered
      .map((p) => Number(p.our_proj || p.projection || 0))
      .filter((v) => v > 0);
    const owns = filtered
      .map((p) => Number(p.our_own || p.market_mean_own || 0))
      .filter((v) => v >= 0);
    if (projs.length === 0) return null;
    const sortedProj = [...projs].sort((a, b) => a - b);
    const sortedOwn = [...owns].sort((a, b) => a - b);
    return {
      maxProj: Math.max(...projs) * 1.05,
      minProj: 0,
      maxOwn: Math.max(50, Math.max(...owns) * 1.1),
      minOwn: 0,
      medianProj: sortedProj[Math.floor(sortedProj.length / 2)],
      medianOwn: sortedOwn[Math.floor(sortedOwn.length / 2)] || 5,
      n: filtered.length,
    };
  }, [filtered]);

  if (!stats) {
    return (
      <div className="border border-zinc-800 bg-[#0e0e0e] p-6 text-center" data-testid="raw-own-curve">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">
          Ownership Curve — load a slate to visualize the leverage landscape
        </div>
      </div>
    );
  }

  const W = 640, H = 320, PAD_L = 40, PAD_R = 20, PAD_T = 20, PAD_B = 30;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const xScale = (own) => PAD_L + ((own - stats.minOwn) / (stats.maxOwn - stats.minOwn)) * plotW;
  const yScale = (proj) => PAD_T + plotH - ((proj - stats.minProj) / (stats.maxProj - stats.minProj)) * plotH;

  const mx = xScale(stats.medianOwn);
  const my = yScale(stats.medianProj);

  // Position color coding for dots (secondary tint on top of quadrant)
  const posColor = (pos) => {
    const p = (pos || "").toUpperCase();
    if (p === "QB") return "#39FF14";
    if (p === "RB") return "#FFCC00";
    if (p === "WR") return "#22D3EE";
    if (p === "TE") return "#F472B6";
    if (p === "DST") return "#FFA500";
    return "#9CA3AF";
  };

  return (
    <div className="border border-zinc-800 bg-[#0e0e0e] p-3" data-testid="raw-own-curve">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="mono text-[11px] font-black uppercase tracking-widest text-white">
          Ownership Curve · Leverage Landscape
        </div>
        <div className="flex items-center gap-1">
          {["ALL", "QB", "RB", "WR", "TE", "DST"].map((pos) => (
            <button
              key={pos}
              type="button"
              onClick={() => setPosFilter(pos)}
              data-testid={`raw-own-curve-pos-${pos}`}
              className={`px-2 py-0.5 mono text-[9px] uppercase tracking-widest border ${
                posFilter === pos
                  ? "bg-[#39FF14] text-black border-[#39FF14] font-bold"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto"
          style={{ maxWidth: "100%" }}
          data-testid="raw-own-curve-svg"
        >
          {/* Quadrant fills */}
          <rect x={PAD_L} y={PAD_T} width={mx - PAD_L} height={my - PAD_T} fill="#39FF14" opacity="0.06" />
          <rect x={mx} y={PAD_T} width={PAD_L + plotW - mx} height={my - PAD_T} fill="#FFCC00" opacity="0.06" />
          <rect x={mx} y={my} width={PAD_L + plotW - mx} height={PAD_T + plotH - my} fill="#FF3B30" opacity="0.06" />
          <rect x={PAD_L} y={my} width={mx - PAD_L} height={PAD_T + plotH - my} fill="#3F3F46" opacity="0.06" />

          {/* Median lines */}
          <line x1={mx} y1={PAD_T} x2={mx} y2={PAD_T + plotH} stroke="#3F3F46" strokeDasharray="3 3" />
          <line x1={PAD_L} y1={my} x2={PAD_L + plotW} y2={my} stroke="#3F3F46" strokeDasharray="3 3" />

          {/* Quadrant labels */}
          <text x={PAD_L + 6} y={PAD_T + 14} fontSize="9" fill="#39FF14" opacity="0.85" fontFamily="monospace" letterSpacing="1">SHARP</text>
          <text x={PAD_L + plotW - 44} y={PAD_T + 14} fontSize="9" fill="#FFCC00" opacity="0.85" fontFamily="monospace" letterSpacing="1">CHALKY</text>
          <text x={PAD_L + plotW - 36} y={PAD_T + plotH - 6} fontSize="9" fill="#FF3B30" opacity="0.85" fontFamily="monospace" letterSpacing="1">TRAP</text>
          <text x={PAD_L + 6} y={PAD_T + plotH - 6} fontSize="9" fill="#71717A" opacity="0.85" fontFamily="monospace" letterSpacing="1">VALUE</text>

          {/* Axes */}
          <line x1={PAD_L} y1={PAD_T + plotH} x2={PAD_L + plotW} y2={PAD_T + plotH} stroke="#52525B" />
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + plotH} stroke="#52525B" />

          {/* Axis ticks */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const own = stats.minOwn + t * (stats.maxOwn - stats.minOwn);
            const x = xScale(own);
            return (
              <g key={`x${t}`}>
                <line x1={x} y1={PAD_T + plotH} x2={x} y2={PAD_T + plotH + 4} stroke="#52525B" />
                <text x={x} y={PAD_T + plotH + 16} fontSize="8" fill="#71717A" textAnchor="middle" fontFamily="monospace">
                  {own.toFixed(0)}%
                </text>
              </g>
            );
          })}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const proj = stats.minProj + t * (stats.maxProj - stats.minProj);
            const y = yScale(proj);
            return (
              <g key={`y${t}`}>
                <line x1={PAD_L - 4} y1={y} x2={PAD_L} y2={y} stroke="#52525B" />
                <text x={PAD_L - 6} y={y + 3} fontSize="8" fill="#71717A" textAnchor="end" fontFamily="monospace">
                  {proj.toFixed(0)}
                </text>
              </g>
            );
          })}
          <text x={PAD_L + plotW / 2} y={H - 4} fontSize="9" fill="#A1A1AA" textAnchor="middle" fontFamily="monospace" letterSpacing="2">OWNERSHIP %</text>
          <text
            x={12}
            y={PAD_T + plotH / 2}
            fontSize="9"
            fill="#A1A1AA"
            textAnchor="middle"
            fontFamily="monospace"
            letterSpacing="2"
            transform={`rotate(-90, 12, ${PAD_T + plotH / 2})`}
          >
            PROJECTION
          </text>

          {/* Data points */}
          {filtered.map((p) => {
            const proj = Number(p.our_proj || p.projection || 0);
            const own = Number(p.our_own || p.market_mean_own || 0);
            if (proj <= 0) return null;
            const cx = xScale(own);
            const cy = yScale(proj);
            const isHl = highlightIds.has(String(p.dk_id));
            return (
              <circle
                key={p.dk_id}
                cx={cx}
                cy={cy}
                r={isHl ? 5 : 3}
                fill={posColor(p.position)}
                stroke={isHl ? "#fff" : "none"}
                strokeWidth={isHl ? 1.5 : 0}
                opacity={isHl ? 1.0 : 0.75}
                style={{ cursor: onPlayerClick ? "pointer" : "default" }}
                onMouseEnter={() => setTooltip({ p, cx, cy })}
                onMouseLeave={() => setTooltip(null)}
                onClick={() => onPlayerClick && onPlayerClick(p)}
                data-testid={`raw-own-curve-dot-${p.dk_id}`}
              />
            );
          })}
        </svg>

        {tooltip && (
          <div
            className="absolute z-10 pointer-events-none bg-black border border-[#39FF14] px-2 py-1 text-[10px] mono uppercase tracking-widest"
            style={{
              left: `${(tooltip.cx / W) * 100}%`,
              top: `${(tooltip.cy / H) * 100}%`,
              transform: "translate(8px, -50%)",
            }}
          >
            <div className="text-white">{tooltip.p.name}</div>
            <div className="text-zinc-400">
              {tooltip.p.position} · {tooltip.p.team} · ${(tooltip.p.salary || 0).toLocaleString()}
            </div>
            <div className="text-[#39FF14]">
              {Number(tooltip.p.our_proj || tooltip.p.projection || 0).toFixed(1)} pts · {Number(tooltip.p.our_own || tooltip.p.market_mean_own || 0).toFixed(1)}%
            </div>
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-3 mono text-[9px] uppercase tracking-widest text-zinc-500">
        <span>{stats.n} players ({posFilter})</span>
        <span>·</span>
        <span>medians: <span className="text-zinc-300">{stats.medianProj.toFixed(1)} pts</span> · <span className="text-zinc-300">{stats.medianOwn.toFixed(1)}%</span></span>
        <span className="ml-auto flex gap-2">
          {[
            { p: "QB", c: "#39FF14" },
            { p: "RB", c: "#FFCC00" },
            { p: "WR", c: "#22D3EE" },
            { p: "TE", c: "#F472B6" },
            { p: "DST", c: "#FFA500" },
          ].map((l) => (
            <span key={l.p} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: l.c }} />
              {l.p}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
